import { Box, Flex, Spinner, Text, VStack } from '@chakra-ui/react';
import { SectionCard, StatusBadge, type StatusType } from '@themes/aegix';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '@aegix/auth-identity';
import {
  ProfileRolesSection,
  ProfileSitesSection,
  type ProfileSite,
} from '@tiaki/profile-ui';
import type { SMSUser } from '../../lib/sms-auth';
import { useUsers } from '../../hooks/queries';
import { useUserSchoolMembership } from '../../hooks/queries/useSchool';
import {
  useReunificationEvents,
  useEventStaff,
} from '../../hooks/queries/useReunification';
import type {
  EventStaffAssignment,
  ReunificationEvent,
} from '../../types/ReunificationTypes';
import { ProfileDutyToggle } from './ProfileDutyToggle';

/**
 * AssignmentsSection — the "what I'm assigned to do / where I belong" view in
 * the Profile drawer. SectionCards, in order:
 *   - Duty             (on/off toggle + off-duty scheduler — gated
 *                      on the two duty permissions; self-hides otherwise)
 *   - Roles            (current user's role/group memberships)
 *   - Sites            (schools the user belongs to — selectable)
 *   - Reunification    (active EventStaffAssignment if a reunification event
 *                      is in progress; precise empty states otherwise)
 *
 * Self-contained: pulls everything from hooks given the current user id.
 * Roles + Sites belong here rather than on the Personal tab — they're
 * semantically "what I'm assigned to," not "who I am."
 *
 * Roles / Sites render from the shared `@tiaki/profile-ui`
 * components (the SMS adapter wires the hooks + maps the
 * `{ school, school_name }` membership shape onto `{ id, name }`).
 * Reunification stays an SMS-only slot.
 *
 * Mirroring AIM, Sites are selectable (`isSiteSelectable`); the
 * `onSelectSite` callback bubbles up to `ProfileDrawer`, which opens a per-site
 * subscriptions sub-drawer. There is no standalone Subscriptions SectionCard —
 * per-site subscriptions live in that sub-drawer.
 *
 * Reunification scope today is the most recent active event only. In practice
 * schools run 0–1 active events at a time. If concurrent active events become
 * common, swap the single-event lookup for `useQueries`.
 */
interface AssignmentsSectionProps {
  /**
   * Called when a site row is selected. `ProfileDrawer` opens the per-site
   * subscriptions sub-drawer. When omitted, site rows are static labels.
   */
  onSelectSite?: (site: ProfileSite) => void;
}

export function AssignmentsSection({ onSelectSite }: AssignmentsSectionProps = {}) {
  const { t } = useTranslation();
  const user = useUser<SMSUser>();
  const currentUserId = user?.id;

  // Full user record — needed for `group_names` (Roles SectionCard).
  const { data: userQuery } = useUsers(
    currentUserId != null ? { ids: String(currentUserId) } : { ids: '' },
  );
  const userDetails = userQuery?.results[0];

  // School memberships — Sites SectionCard.
  const { data: schoolMembership } = useUserSchoolMembership({
    baseuser: currentUserId ?? 0,
  });

  // The user's sites — the Sites list rows (selectable, each opening a
  // per-site subscriptions sub-drawer via `onSelectSite`).
  const siteOptions = useMemo<ProfileSite[]>(
    () =>
      (schoolMembership?.results ?? []).map((s) => ({
        id: s.school,
        name: s.school_name,
      })),
    [schoolMembership],
  );

  // Reunification: most recent active event, then its staff list filtered to
  // the current user.
  const eventsQuery = useReunificationEvents({
    status: 'active',
    page_size: 1,
    ordering: '-started_at',
  });
  const activeEvent: ReunificationEvent | undefined =
    eventsQuery.data?.results?.[0];

  const staffQuery = useEventStaff(activeEvent?.public_id ?? '', {
    page_size: 100,
  });

  const myReunifyAssignments = useMemo<EventStaffAssignment[]>(() => {
    if (!activeEvent || !staffQuery.data?.results || currentUserId == null) {
      return [];
    }
    return staffQuery.data.results.filter(
      (a) => a.staff_member === currentUserId,
    );
  }, [activeEvent, staffQuery.data, currentUserId]);

  return (
    <VStack align="stretch" gap={4}>
      {/* Duty on/off toggle + off-duty scheduler, mirroring AIM.
          Self-hides when the user/role lacks the two duty permissions
          (`accounts.view_userduty` + `accounts.change_userduty`, matching
          iOS). `user_duty_id` comes from the auth session
          (`useUser<SMSUser>()` inside the adapter), not the full user record.
          Rendered first — Duty → Roles → Sites → Reunification. */}
      <ProfileDutyToggle />
      <ProfileRolesSection roles={userDetails?.group_names} />
      {/* Site rows are selectable; picking one opens a per-site
          subscriptions sub-drawer in `ProfileDrawer` (mirroring AIM). The
          membership `{ school, school_name }` shape is mapped onto
          `{ id, name }`. */}
      <ProfileSitesSection
        sites={siteOptions}
        isSiteSelectable={true}
        onSelectSite={onSelectSite}
      />
      <SectionCard title={t('reunification.title', 'Reunification')}>
        <ReunificationAssignments
          eventsLoading={eventsQuery.isLoading}
          activeEvent={activeEvent}
          staffLoading={staffQuery.isLoading}
          assignments={myReunifyAssignments}
        />
      </SectionCard>
    </VStack>
  );
}

interface ReunificationAssignmentsProps {
  eventsLoading: boolean;
  activeEvent: ReunificationEvent | undefined;
  staffLoading: boolean;
  assignments: EventStaffAssignment[];
}

function ReunificationAssignments({
  eventsLoading,
  activeEvent,
  staffLoading,
  assignments,
}: ReunificationAssignmentsProps) {
  const { t } = useTranslation();

  if (eventsLoading) {
    return (
      <Flex justify="center" py={4}>
        <Spinner size="sm" />
      </Flex>
    );
  }

  if (!activeEvent) {
    return (
      <Box py={2}>
        <Text color="text.secondary" fontSize="sm">
          {t('reunification.noActiveEvent', 'No active reunification event.')}
        </Text>
        <Text color="text.tertiary" fontSize="xs" mt={1}>
          {t(
            'reunification.assignmentsWillAppear',
            'Your assignments will appear here when an event is activated.',
          )}
        </Text>
      </Box>
    );
  }

  if (staffLoading) {
    return (
      <Flex justify="center" py={4}>
        <Spinner size="sm" />
      </Flex>
    );
  }

  if (assignments.length === 0) {
    return (
      <Box py={2}>
        <Text color="text.secondary" fontSize="sm">
          {t('reunification.noAssignmentsForYou', 'No assignments for you in')}{' '}
          <strong>{activeEvent.school_name}</strong>.
        </Text>
        <Text color="text.tertiary" fontSize="xs" mt={1}>
          {t(
            'reunification.notAssigned',
            'You are not currently assigned to a station or role in the active event.',
          )}
        </Text>
      </Box>
    );
  }

  return (
    <VStack align="stretch" gap={3} py={2}>
      <Text fontSize="xs" color="text.tertiary" textTransform="uppercase">
        {t('reunification.activeEvent', 'Active event')}: {activeEvent.school_name}
      </Text>
      {assignments.map((assignment) => (
        <Box
          key={assignment.public_id}
          p={4}
          borderWidth="1px"
          borderColor="border.primary"
          borderRadius="md"
          bg="surface.primary"
          data-e2e={`assignment-${assignment.public_id}`}
        >
          <Flex justify="space-between" align="start" mb={2}>
            <Text fontSize="md" fontWeight={600} color="text.primary">
              {assignment.role_display || assignment.role}
            </Text>
            <StatusBadge
              status={statusType(assignment.status)}
              label={assignment.status_display || assignment.status}
            />
          </Flex>
          {assignment.station_name && (
            <Text fontSize="sm" color="text.secondary" mb={1}>
              {t('reunification.station', 'Station')}: {assignment.station_name}
            </Text>
          )}
          {assignment.checked_in_at && (
            <Text fontSize="xs" color="text.tertiary">
              {t('reunification.checkedIn', 'Checked in')}{' '}
              {formatRelative(assignment.checked_in_at)}
            </Text>
          )}
          {assignment.notes && (
            <Text fontSize="xs" color="text.secondary" mt={2} fontStyle="italic">
              {assignment.notes}
            </Text>
          )}
        </Box>
      ))}
    </VStack>
  );
}

/**
 * Map a reunification staff status onto a canonical `StatusType`
 * (ADR-118 — `StatusBadge` owns the color map). Preserves the prior
 * green / orange / gray / red semantics: present → green, awaiting →
 * orange, terminal → gray, unavailable → red. `assignment.status` is
 * `StaffStatus` today; the extra cases + `default` keep the mapper
 * robust to backend-side status additions.
 */
function statusType(status: string): StatusType {
  switch (status) {
    case 'checked_in':
    case 'active':
      return 'active'; // green
    case 'assigned':
    case 'pending':
      return 'warning'; // orange
    case 'released':
    case 'completed':
      return 'default'; // gray
    case 'absent':
    case 'unable':
      return 'error'; // red
    default:
      return 'default'; // gray
  }
}

function formatRelative(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}
